import { useEffect, useRef, useState } from "react";
import {
  PenTool, Zap, Package, Calendar, ArrowRight, ArrowDown,
  MapPin, Mail, Check,
  Menu, X, Image as ImageIcon, ShieldCheck, Truck,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────────
   NEON FORGE — single-page site (preview build)

   Motion here is hand-rolled (IntersectionObserver + rAF) so the canvas
   runs dependency-free. In the production Vite build, each piece maps 1:1
   onto the brief's stack:

     <Reveal> / useInView .......... framer-motion `whileInView`
     .neon-h power-on keyframes .... framer-motion variants (same timings)
     hero parallax rAF loop ........ Lenis scroll event → transform
     cursor-glow rAF loop .......... GSAP quickTo(x, y)

   Timings follow the spec: 300–600ms ease-out, 70ms stagger,
   prefers-reduced-motion respected everywhere.
   ───────────────────────────────────────────────────────────────────────── */

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Unbounded:wght@600;800&family=Geist:wght@400;500;600&family=Pacifico&display=swap');

:root{
  --bg:#0A0A0B;
  --panel:#0F0F13;
  --panel-2:#0C0C0F;
  --line:rgba(255,255,255,.07);
  --text:#ECECF1;
  --muted:#A3A3AE;
  --dim:#67676F;
  --neon:255,45,149;        /* magenta — hero accent */
  --neon-core:#FFD9EC;
  --cyan:0,229,255;         /* secondary — used sparingly */
  --cyan-core:#D9FBFF;
  --warm:255,196,110;       /* tube option only, never chrome */
  --warm-core:#FFF6E3;
  --ease:cubic-bezier(.22,1,.36,1);
}

*{box-sizing:border-box;margin:0;padding:0}
html{scroll-behavior:smooth}
body{background:var(--bg)}

.nf{
  background:var(--bg);
  color:var(--text);
  font-family:'Geist',ui-sans-serif,system-ui,sans-serif;
  font-size:16px;
  line-height:1.7;
  overflow-x:hidden;
  min-height:100vh;
}
.nf ::selection{background:rgba(var(--neon),.35);color:#fff}
.nf a{color:inherit;text-decoration:none}
.nf button{font:inherit;color:inherit;background:none;border:none;cursor:pointer}
.nf :focus-visible{outline:1px solid rgba(var(--neon),.7);outline-offset:3px;border-radius:4px}

section[id]{scroll-margin-top:76px}

/* ── ambient scene ─────────────────────────────────────────── */
.pools{position:fixed;inset:0;pointer-events:none;z-index:0}
.pools::before{
  content:"";position:absolute;top:-20%;left:-15%;width:60vw;height:60vw;
  background:radial-gradient(circle,rgba(var(--neon),.05),transparent 65%);
}
.pools::after{
  content:"";position:absolute;bottom:-25%;right:-18%;width:55vw;height:55vw;
  background:radial-gradient(circle,rgba(var(--cyan),.035),transparent 65%);
}
.grain{
  position:fixed;inset:0;pointer-events:none;z-index:60;opacity:.045;
  mix-blend-mode:overlay;
  background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/><feColorMatrix type='saturate' values='0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.55'/></svg>");
}
.cursor-glow{
  position:fixed;top:0;left:0;width:520px;height:520px;z-index:70;
  pointer-events:none;opacity:0;transition:opacity .6s ease-out;
  background:radial-gradient(circle,rgba(var(--neon),.12),transparent 62%);
  mix-blend-mode:screen;will-change:transform;
}

/* ── type ──────────────────────────────────────────────────── */
.display{font-family:'Unbounded',sans-serif;font-weight:800;letter-spacing:-.01em;color:#fff}
.overline{
  font-size:11px;font-weight:500;letter-spacing:.34em;text-transform:uppercase;
  color:var(--dim);
}
.h2{font-size:clamp(1.7rem,4vw,2.8rem);line-height:1.12}
.lede{color:var(--muted);max-width:34rem}
.tube{font-family:'Pacifico',cursive;font-weight:400;line-height:1.25}
.tube-m{color:var(--neon-core);text-shadow:0 0 6px rgba(var(--neon),.9),0 0 20px rgba(var(--neon),.55),0 0 48px rgba(var(--neon),.3),0 0 96px rgba(var(--neon),.16)}
.tube-c{color:var(--cyan-core);text-shadow:0 0 6px rgba(var(--cyan),.9),0 0 20px rgba(var(--cyan),.5),0 0 48px rgba(var(--cyan),.28),0 0 96px rgba(var(--cyan),.15)}
.tube-w{color:var(--warm-core);text-shadow:0 0 6px rgba(var(--warm),.9),0 0 20px rgba(var(--warm),.5),0 0 48px rgba(var(--warm),.26),0 0 90px rgba(var(--warm),.14)}
.tube-live{text-shadow:0 0 6px rgba(var(--g),.9),0 0 20px rgba(var(--g),.55),0 0 48px rgba(var(--g),.3),0 0 96px rgba(var(--g),.16)}

/* ── scroll choreography ───────────────────────────────────── */
.reveal{opacity:0;transform:translateY(22px);transition:opacity .5s var(--ease),transform .55s var(--ease)}
.reveal.in{opacity:1;transform:none}

.neon-h{opacity:0}
.neon-h.on{animation:powerOn .85s linear forwards}
@keyframes powerOn{
  0%   {opacity:0;   text-shadow:none}
  7%   {opacity:.55; text-shadow:0 0 14px rgba(var(--neon),.55)}
  11%  {opacity:.06; text-shadow:none}
  21%  {opacity:.75; text-shadow:0 0 16px rgba(var(--neon),.6)}
  27%  {opacity:.15; text-shadow:none}
  38%,100%{opacity:1;text-shadow:0 0 10px rgba(var(--neon),.4),0 0 34px rgba(var(--neon),.18)}
}

@keyframes ambient{
  0%,88%,100%{opacity:1}
  89%{opacity:.84} 90%{opacity:1} 94.5%{opacity:.93} 95.5%{opacity:1}
}
@keyframes tubeDip{
  0%,86%,100%{opacity:1}
  87%{opacity:.45} 88%{opacity:1} 89.5%{opacity:.6} 90.5%{opacity:1}
}
.ambient{animation:ambient 8s ease-in-out infinite}
.tube-dip{animation:tubeDip 11s linear infinite;display:inline-block}

@keyframes cuePulse{0%,100%{opacity:.35;transform:translateY(0)}50%{opacity:.9;transform:translateY(6px)}}

/* ── nav ───────────────────────────────────────────────────── */
.nav{
  position:fixed;top:0;left:0;right:0;z-index:50;
  transition:background .4s ease-out,border-color .4s ease-out,backdrop-filter .4s;
  border-bottom:1px solid transparent;
}
.nav.scrolled{background:rgba(10,10,11,.72);backdrop-filter:blur(14px);border-color:var(--line)}
.nav-in{max-width:72rem;margin:0 auto;padding:0 24px;height:68px;display:flex;align-items:center;justify-content:space-between}
.logo{font-family:'Unbounded',sans-serif;font-weight:800;font-size:15px;letter-spacing:.06em;color:#fff;display:flex;align-items:center;gap:10px}
.logo i{width:7px;height:7px;border-radius:50%;background:rgb(var(--neon));box-shadow:0 0 10px rgba(var(--neon),.9),0 0 22px rgba(var(--neon),.5)}
.nav-links{display:flex;gap:34px}
.nav-links button{font-size:13.5px;font-weight:500;color:var(--muted);letter-spacing:.04em;transition:color .3s ease-out}
.nav-links button:hover{color:#fff}

.nav-toggle{display:none;align-items:center;justify-content:center;width:42px;height:42px;border:1px solid var(--line);border-radius:10px;color:var(--text)}
.nav-toggle:hover{border-color:rgba(255,255,255,.22)}
.mobile-menu{
  overflow:hidden;max-height:0;opacity:0;
  background:rgba(10,10,11,.92);backdrop-filter:blur(14px);border-bottom:1px solid transparent;
  transition:max-height .38s var(--ease),opacity .3s ease-out,border-color .3s;
}
.mobile-menu.open{max-height:360px;opacity:1;border-color:var(--line)}
.mobile-menu ul{list-style:none;max-width:72rem;margin:0 auto;padding:8px 24px 20px;display:flex;flex-direction:column}
.mobile-menu button{width:100%;text-align:left;padding:15px 2px;font-size:16px;font-weight:500;color:var(--text);border-bottom:1px solid var(--line);display:flex;justify-content:space-between;align-items:center}
.mobile-menu button:last-child{border-bottom:none}
.mobile-menu .m-cta{color:var(--neon-core);text-shadow:0 0 12px rgba(var(--neon),.5)}

@media(max-width:820px){
  .nav-links{display:none}
  .nav .btn-sm{display:none}
  .nav-toggle{display:inline-flex}
}
@media(min-width:821px){.mobile-menu{display:none}}

/* ── buttons ───────────────────────────────────────────────── */
.btn{
  display:inline-flex;align-items:center;gap:9px;border-radius:999px;
  padding:13px 26px;font-size:14.5px;font-weight:500;letter-spacing:.02em;
  transition:transform .3s var(--ease),box-shadow .35s ease-out,background .3s ease-out,border-color .3s,color .3s;
  white-space:nowrap;
}
.btn-neon{
  color:var(--neon-core);border:1px solid rgba(var(--neon),.55);
  background:rgba(var(--neon),.07);
  text-shadow:0 0 12px rgba(var(--neon),.5);
  box-shadow:0 0 22px rgba(var(--neon),.14),inset 0 0 14px rgba(var(--neon),.07);
}
.btn-neon:hover{
  background:rgba(var(--neon),.15);transform:translateY(-1px);
  box-shadow:0 0 40px rgba(var(--neon),.34),inset 0 0 18px rgba(var(--neon),.14);
}
.btn-ghost{color:var(--muted);border:1px solid var(--line)}
.btn-ghost:hover{color:#fff;border-color:rgba(255,255,255,.22)}
.btn-sm{padding:10px 20px;font-size:13px}

/* ── layout primitives ─────────────────────────────────────── */
.wrap{max-width:72rem;margin:0 auto;padding:0 24px;position:relative;z-index:1}
.sect{padding:120px 0}
@media(max-width:640px){.sect{padding:88px 0}}
.head{display:flex;flex-direction:column;gap:16px;margin-bottom:56px}
.rule{height:1px;background:linear-gradient(90deg,transparent,rgba(var(--neon),.28),transparent);border:none}

/* ── hero ──────────────────────────────────────────────────── */
.hero{min-height:100vh;display:flex;align-items:center;justify-content:center;position:relative;padding:120px 24px 90px;text-align:center}
.hero-vig{position:absolute;inset:0;pointer-events:none;background:radial-gradient(ellipse 90% 70% at 50% 42%,transparent 55%,rgba(0,0,0,.55) 100%)}
.hero-inner{position:relative;z-index:2;display:flex;flex-direction:column;align-items:center;gap:0;max-width:60rem}
.hero-sign{font-size:clamp(3.4rem,11.5vw,8.6rem);margin:26px 0 4px;will-change:transform;position:relative;z-index:2}
.floor-glow{
  width:min(68vw,640px);height:110px;margin-top:-38px;pointer-events:none;
  background:radial-gradient(ellipse at 50% 0%,rgba(var(--neon),.16),transparent 68%);
  filter:blur(6px);will-change:transform;
}
.hero-tag{color:var(--muted);font-size:clamp(1rem,1.6vw,1.15rem);max-width:32rem;margin-top:6px}
.hero-ctas{display:flex;gap:16px;margin-top:38px;flex-wrap:wrap;justify-content:center}
.scroll-cue{position:absolute;bottom:30px;left:50%;transform:translateX(-50%);color:var(--dim);display:flex;flex-direction:column;align-items:center;gap:8px;font-size:10.5px;letter-spacing:.3em;text-transform:uppercase}
.scroll-cue svg{animation:cuePulse 2.4s ease-in-out infinite}

/* ── trust strip ───────────────────────────────────────────── */
.trust{border-top:1px solid var(--line);border-bottom:1px solid var(--line);padding:44px 0;background:var(--panel-2)}
.trust-row{display:flex;flex-wrap:wrap;gap:20px 56px;justify-content:center;margin-top:26px}
.trust-row span{
  font-family:'Unbounded',sans-serif;font-weight:600;font-size:13px;letter-spacing:.14em;
  color:var(--dim);transition:color .3s ease-out,text-shadow .3s ease-out;
}
.trust-row span:hover{color:#cfcfd6}

/* ── tiers ─────────────────────────────────────────────────── */
.tiers{display:grid;grid-template-columns:repeat(3,1fr);gap:22px}
@media(max-width:900px){.tiers{grid-template-columns:1fr;max-width:26rem;margin:0 auto}}
.card{
  position:relative;border-radius:20px;padding:34px 30px;
  background:linear-gradient(180deg,var(--panel),var(--panel-2));
  border:1px solid var(--line);display:flex;flex-direction:column;gap:0;
  transition:transform .35s var(--ease),box-shadow .4s ease-out,border-color .35s;
}
.card:hover{
  transform:translateY(-5px) scale(1.015);
  border-color:rgba(var(--neon),.32);
  box-shadow:0 0 0 1px rgba(var(--neon),.14),0 14px 50px rgba(var(--neon),.1),0 30px 80px rgba(0,0,0,.55);
}
.card.feat{border-color:rgba(var(--neon),.42);box-shadow:0 0 0 1px rgba(var(--neon),.16),0 0 44px rgba(var(--neon),.1)}
.card.feat:hover{box-shadow:0 0 0 1px rgba(var(--neon),.28),0 16px 60px rgba(var(--neon),.16),0 30px 80px rgba(0,0,0,.55)}
.tag{
  position:absolute;top:-11px;left:30px;font-size:10px;font-weight:600;letter-spacing:.26em;text-transform:uppercase;
  padding:5px 12px;border-radius:999px;background:#17070f;color:var(--neon-core);
  border:1px solid rgba(var(--neon),.5);text-shadow:0 0 10px rgba(var(--neon),.6);
}
.tier-name{font-family:'Unbounded',sans-serif;font-weight:600;font-size:15px;letter-spacing:.1em;color:#fff;text-transform:uppercase}
.price{font-family:'Unbounded',sans-serif;font-weight:800;font-size:clamp(1.7rem,2.6vw,2.1rem);color:#fff;margin:14px 0 2px}
.price small{font-size:.5em;font-weight:600;color:var(--dim);letter-spacing:.08em}
.card p{color:var(--muted);font-size:14.5px;margin:10px 0 22px}
.feats{list-style:none;display:flex;flex-direction:column;gap:11px;margin-bottom:30px;flex:1}
.feats li{display:flex;gap:11px;align-items:flex-start;font-size:13.8px;color:#c9c9d2}
.feats svg{flex:none;margin-top:4px;color:rgba(var(--neon),.85)}

/* ── gallery ───────────────────────────────────────────────── */
.masonry{columns:3;column-gap:20px}
@media(max-width:980px){.masonry{columns:2}}
@media(max-width:600px){.masonry{columns:1}}
.tile{
  break-inside:avoid;margin-bottom:20px;border-radius:18px;overflow:hidden;position:relative;
  background:radial-gradient(120% 130% at 50% 0%,#131318 0%,#0B0B0E 60%);
  border:1px solid var(--line);display:flex;align-items:center;justify-content:center;text-align:center;
  transition:transform .35s var(--ease),border-color .35s,box-shadow .4s ease-out;
}
.tile:hover{transform:translateY(-4px);border-color:rgba(255,255,255,.16);box-shadow:0 18px 60px rgba(0,0,0,.55)}
.tile .word{transition:filter .35s ease-out;padding:0 22px}
.tile:hover .word{filter:brightness(1.18)}
.cap{
  position:absolute;left:18px;bottom:14px;right:18px;text-align:left;
  font-size:11.5px;letter-spacing:.06em;color:var(--dim);
  display:flex;justify-content:space-between;gap:12px;
}
.cap b{color:#b9b9c2;font-weight:500}
.tile img{display:block;width:100%;height:auto}
.ph{
  display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;
  width:100%;color:var(--dim);
}
.ph span{font-size:11px;letter-spacing:.22em;text-transform:uppercase}
.tile:has(.ph){background:repeating-linear-gradient(45deg,#0E0E12,#0E0E12 12px,#0B0B0E 12px,#0B0B0E 24px)}

/* ── process ───────────────────────────────────────────────── */
.steps{display:grid;grid-template-columns:repeat(3,1fr);gap:22px;position:relative}
@media(max-width:900px){.steps{grid-template-columns:1fr;max-width:30rem}}
.step{position:relative;padding:30px 28px;border:1px solid var(--line);border-radius:18px;background:var(--panel-2)}
.step-n{font-family:'Unbounded',sans-serif;font-weight:800;font-size:13px;color:rgba(var(--neon),.75);letter-spacing:.2em}
.step-ic{width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;margin:20px 0 16px;border:1px solid rgba(var(--neon),.3);background:rgba(var(--neon),.06);color:var(--neon-core)}
.step h3{font-family:'Unbounded',sans-serif;font-weight:600;font-size:17px;color:#fff;margin-bottom:10px}
.step p{color:var(--muted);font-size:14.5px}

/* ── configurator ──────────────────────────────────────────── */
.config{display:grid;grid-template-columns:1fr 1.15fr;gap:40px;align-items:stretch}
@media(max-width:900px){.config{grid-template-columns:1fr}}
.panel{border:1px solid var(--line);border-radius:20px;background:var(--panel-2);padding:32px}
.field{margin-bottom:26px}
.label{display:block;font-size:11px;font-weight:600;letter-spacing:.24em;text-transform:uppercase;color:var(--dim);margin-bottom:12px}
.tinput{
  width:100%;background:#08080A;border:1px solid var(--line);border-radius:12px;
  padding:14px 16px;color:#fff;font-family:'Geist',sans-serif;font-size:15px;
  transition:border-color .3s,box-shadow .3s;
}
.tinput:focus{outline:none;border-color:rgba(var(--neon),.55);box-shadow:0 0 0 3px rgba(var(--neon),.12)}
.swatches{display:flex;gap:14px}
.sw{width:38px;height:38px;border-radius:50%;border:1px solid var(--line);position:relative;transition:transform .25s var(--ease),box-shadow .3s}
.sw:hover{transform:scale(1.1)}
.sw.on{box-shadow:0 0 0 2px var(--bg),0 0 0 4px rgba(var(--sg),.8),0 0 18px rgba(var(--sg),.6)}
.seg{display:flex;border:1px solid var(--line);border-radius:12px;overflow:hidden}
.seg button{flex:1;padding:12px 8px;font-size:13px;color:var(--muted);transition:background .3s,color .3s;border-right:1px solid var(--line)}
.seg button:last-child{border-right:none}
.seg button.on{background:rgba(var(--neon),.12);color:var(--neon-core)}
.seg small{display:block;font-size:10.5px;color:var(--dim);letter-spacing:.06em;margin-top:2px}
.preview{
  border:1px solid var(--line);border-radius:20px;position:relative;overflow:hidden;
  background:radial-gradient(110% 120% at 50% 8%,#15151b 0%,#09090B 62%);
  display:flex;flex-direction:column;align-items:center;justify-content:center;
  min-height:380px;padding:48px 28px;text-align:center;
}
.preview .brick{position:absolute;inset:0;opacity:.05;background:repeating-linear-gradient(0deg,transparent,transparent 26px,rgba(255,255,255,.5) 26px,rgba(255,255,255,.5) 27px)}
.est{position:absolute;left:24px;bottom:20px;right:24px;display:flex;justify-content:space-between;align-items:baseline;gap:14px;text-align:left}
.est span{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--dim)}
.est b{font-family:'Unbounded',sans-serif;font-weight:800;font-size:1.25rem;color:#fff}

/* ── testimonials ──────────────────────────────────────────── */
.quotes{display:grid;grid-template-columns:repeat(3,1fr);gap:22px}
@media(max-width:900px){.quotes{grid-template-columns:1fr;max-width:30rem}}
.quote{padding:30px 28px;border:1px solid var(--line);border-radius:18px;background:var(--panel-2);display:flex;flex-direction:column;gap:16px}
.quote svg{color:rgba(var(--neon),.8);filter:drop-shadow(0 0 8px rgba(var(--neon),.5))}
.quote p{color:#d4d4dc;font-size:15px}
.quote footer{font-size:12.5px;color:var(--dim);letter-spacing:.04em}
.why-ic{width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;border:1px solid rgba(var(--neon),.3);background:rgba(var(--neon),.06);color:var(--neon-core)}
.why-h{font-family:'Unbounded',sans-serif;font-weight:600;font-size:16.5px;color:#fff}

/* ── booking ───────────────────────────────────────────────── */
.book{display:grid;grid-template-columns:1fr 1fr;gap:44px;align-items:start}
@media(max-width:900px){.book{grid-template-columns:1fr}}
.funnel{list-style:none;display:flex;flex-direction:column;gap:0;margin-top:34px}
.funnel li{display:flex;gap:20px;padding:22px 0;border-top:1px solid var(--line)}
.funnel li:last-child{border-bottom:1px solid var(--line)}
.fn{font-family:'Unbounded',sans-serif;font-weight:800;font-size:12px;color:rgba(var(--neon),.8);padding-top:4px;letter-spacing:.12em}
.funnel h4{font-size:15.5px;font-weight:600;color:#fff;margin-bottom:5px}
.funnel p{font-size:13.8px;color:var(--muted)}
.cal-box{
  border:1px dashed rgba(255,255,255,.16);border-radius:20px;min-height:380px;
  display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;
  background:var(--panel-2);text-align:center;padding:40px 28px;
}
.cal-box .mono{font-family:ui-monospace,monospace;font-size:11.5px;color:var(--dim);background:#08080A;border:1px solid var(--line);border-radius:8px;padding:8px 14px}

/* ── footer ────────────────────────────────────────────────── */
.footer{border-top:1px solid var(--line);padding:70px 0 40px;background:var(--panel-2)}
.f-grid{display:grid;grid-template-columns:2fr 1fr 1fr;gap:40px}
@media(max-width:800px){.f-grid{grid-template-columns:1fr}}
.f-col h5{font-size:11px;font-weight:600;letter-spacing:.26em;text-transform:uppercase;color:var(--dim);margin-bottom:18px}
.f-col ul{list-style:none;display:flex;flex-direction:column;gap:12px}
.f-col li,.f-col a{display:flex;align-items:center;gap:10px;font-size:14px;color:var(--muted)}
.f-col a:hover{color:#fff}
.f-col svg{color:var(--dim)}
.f-base{display:flex;justify-content:space-between;gap:16px;flex-wrap:wrap;margin-top:56px;padding-top:26px;border-top:1px solid var(--line);font-size:12.5px;color:var(--dim);letter-spacing:.04em}

/* ── reduced motion ────────────────────────────────────────── */
@media (prefers-reduced-motion: reduce){
  html{scroll-behavior:auto}
  .nf *,.nf *::before,.nf *::after{animation-duration:.001s!important;animation-iteration-count:1!important;transition-duration:.001s!important}
  .reveal{opacity:1;transform:none}
  .neon-h{opacity:1;text-shadow:0 0 10px rgba(var(--neon),.35)}
  .cursor-glow{display:none}
}
`;

/* ── motion hooks ─────────────────────────────────────────────────────── */

const prefersReduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function useInView(threshold = 0.25) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReduced()) { setInView(true); return; }
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } },
      { threshold, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* Staggered reveal — production equivalent: framer-motion whileInView */
function Reveal({ children, delay = 0, className = "" }) {
  const [ref, inView] = useInView(0.2);
  return (
    <div
      ref={ref}
      className={`reveal ${inView ? "in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* Section heading with the signature power-on flicker */
function NeonH({ children }) {
  const [ref, inView] = useInView(0.6);
  return (
    <h2 ref={ref} className={`display h2 neon-h ${inView ? "on" : ""}`}>
      {children}
    </h2>
  );
}

function SectionHead({ overline, title, lede }) {
  return (
    <div className="head">
      <Reveal><div className="overline">{overline}</div></Reveal>
      <NeonH>{title}</NeonH>
      {lede && <Reveal delay={90}><p className="lede">{lede}</p></Reveal>}
    </div>
  );
}

const go = (id) =>
  document.getElementById(id)?.scrollIntoView({
    behavior: prefersReduced() ? "auto" : "smooth",
  });

/* ── content ──────────────────────────────────────────────────────────── */

/* Honest signals — every one of these is true of how the sign is actually
   made and delivered. Replace with a real client logo wall once you have
   permission to show customers' names. */
const TRUST = [
  "HANDMADE IN PEENYA", "LASER-CUT ACRYLIC", "FLICKER-FREE DIMMING",
  "24-HOUR BURN-IN TEST", "SHIPS PAN-INDIA", "INSTALLED IN BENGALURU",
];

const TIERS = [
  {
    name: "Spark", price: "₹18,000", from: "from", tag: null,
    blurb: "A single word or short phrase. The fastest way to put light on a wall.",
    feats: ["Up to 45 cm · one colour", "Clear acrylic backing", "Dimmer + 2 m cable", "Wall-mount kit included", "Ships in 7–10 days"],
  },
  {
    name: "Signature", price: "₹40,000", from: "from", tag: "Most booked",
    blurb: "Statement pieces for cafés, studios and storefronts. Built to be photographed.",
    feats: ["Up to 90 cm · two colours", "Cut-to-shape acrylic", "Flicker-free dimming", "Two mockup revisions", "Install in Bengaluru included"],
    featured: true,
  },
  {
    name: "Bespoke", price: "₹1,00,000", from: "from", tag: null,
    blurb: "Logos, murals and multi-element installs. We design the light around the space.",
    feats: ["Any size · layered builds", "Logo & artwork recreation", "RGB / tunable options", "Site visit + lighting plan", "Dedicated build lead"],
  },
];

/* Real photos go here. Drop your A6400 shots into /public and set `src`.
   `caption` and `dim` stay empty until they describe an actual build —
   nothing here should claim a client or project you don't have yet.
   Leave `src` null to show the "add photo" placeholder while you shoot. */
const GALLERY = [
  { src: null, h: 300, caption: "", dim: "" },
  { src: null, h: 220, caption: "", dim: "" },
  { src: null, h: 360, caption: "", dim: "" },
  { src: null, h: 240, caption: "", dim: "" },
  { src: null, h: 300, caption: "", dim: "" },
  { src: null, h: 260, caption: "", dim: "" },
];

const STEPS = [
  { n: "01", Icon: PenTool, title: "Design",
    body: "Tell us the idea — a word, a logo, a feeling. You get a to-scale mockup in 48 hours and two rounds of tweaks." },
  { n: "02", Icon: Zap, title: "Forge",
    body: "Laser-cut acrylic, hand-bent LED flex, soldered and sealed at our Peenya workshop. Every sign runs a 24-hour burn-in test." },
  { n: "03", Icon: Package, title: "Deliver",
    body: "Crated and couriered pan-India, or installed by our team in Bengaluru. Ten to fourteen days, door to door." },
];

/* Honest founder-voice differentiators — no invented customers. Swap this
   whole section for real testimonials (with names + permission) once you
   have them; the grid layout is already built for quote cards. */
const WHY = [
  { Icon: PenTool, title: "Mockup before you commit",
    body: "You approve a to-scale mockup and up to two revisions before we cut a thing. No surprises on the wall." },
  { Icon: ShieldCheck, title: "Built to run for years",
    body: "Every sign is soldered, sealed and burned in for 24 hours before it leaves Peenya. Flicker-free dimming on every build." },
  { Icon: Truck, title: "We handle the last mile",
    body: "Crated and couriered pan-India, or installed by our own team in Bengaluru. One point of contact from sketch to switch-on." },
];

const COLORS = [
  { name: "Magenta",    core: "#FFD9EC", g: "255,45,149",  chip: "#FF2D95" },
  { name: "Cyan",       core: "#D9FBFF", g: "0,229,255",   chip: "#00E5FF" },
  { name: "Warm white", core: "#FFF6E3", g: "255,196,110", chip: "#FFC46E" },
  { name: "Violet",     core: "#ECDFFF", g: "168,85,247",  chip: "#A855F7" },
];

const SIZES = [
  { id: "S", label: "Small",  sub: "~45 cm",  fs: "2.1rem", base: 18000 },
  { id: "M", label: "Medium", sub: "~75 cm",  fs: "3rem",   base: 36000 },
  { id: "L", label: "Large",  sub: "120 cm+", fs: "4rem",   base: 72000 },
];

const inr = (n) => "₹" + n.toLocaleString("en-IN");

/* ── sections ─────────────────────────────────────────────────────────── */

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 12);
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  const links = [["Work", "work"], ["Pricing", "pricing"], ["Process", "process"], ["Design", "design"]];
  const jump = (id) => { setOpen(false); go(id); };
  return (
    <nav className={`nav ${scrolled ? "scrolled" : ""}`}>
      <div className="nav-in">
        <button className="logo" onClick={() => jump("top")} aria-label="Neon Forge — back to top">
          <i /> NEON FORGE
        </button>
        <div className="nav-links">
          {links.map(([l, id]) => (
            <button key={id} onClick={() => jump(id)}>{l}</button>
          ))}
        </div>
        <button className="btn btn-neon btn-sm" onClick={() => jump("book")}>
          Book a call <ArrowRight size={15} />
        </button>
        <button
          className="nav-toggle"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      <div className={`mobile-menu ${open ? "open" : ""}`} id="mobile-menu">
        <ul>
          {links.map(([l, id]) => (
            <li key={id}>
              <button onClick={() => jump(id)}>{l} <ArrowRight size={16} /></button>
            </li>
          ))}
          <li>
            <button className="m-cta" onClick={() => jump("book")}>Book a call <ArrowRight size={16} /></button>
          </li>
        </ul>
      </div>
    </nav>
  );
}

function Hero() {
  const signRef = useRef(null);
  const glowRef = useRef(null);

  /* Parallax — production equivalent: Lenis scroll → transform */
  useEffect(() => {
    if (prefersReduced()) return;
    let target = 0, cur = 0, raf;
    const onScroll = () => { target = window.scrollY; };
    const tick = () => {
      cur += (target - cur) * 0.08;
      if (signRef.current) signRef.current.style.transform = `translateY(${cur * 0.18}px)`;
      if (glowRef.current) glowRef.current.style.transform = `translateY(${cur * 0.3}px)`;
      raf = requestAnimationFrame(tick);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    tick();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("scroll", onScroll); };
  }, []);

  return (
    <section className="hero" id="top">
      <div className="hero-vig" />
      <div className="hero-inner">
        <Reveal><div className="overline">Bengaluru · Handcrafted LED Neon</div></Reveal>

        {/* The sign itself, and the page's single h1 — ambient flicker loop, one tube section dips */}
        <h1 ref={signRef} className="hero-sign tube tube-m ambient">
          Neon&nbsp;<span className="tube-dip">Forge</span>
        </h1>
        <div ref={glowRef} className="floor-glow" aria-hidden="true" />

        <Reveal delay={140}>
          <p className="hero-tag">
            Custom LED neon, forged in Bengaluru. For cafés, studios and spaces
            that want to be remembered after dark.
          </p>
        </Reveal>
        <Reveal delay={220}>
          <div className="hero-ctas">
            <button className="btn btn-neon" onClick={() => go("design")}>
              Design yours <ArrowRight size={16} />
            </button>
            <button className="btn btn-ghost" onClick={() => go("work")}>
              See the work
            </button>
          </div>
        </Reveal>
      </div>
      <button className="scroll-cue" onClick={() => go("trust")} aria-label="Scroll down">
        Scroll <ArrowDown size={14} />
      </button>
    </section>
  );
}

function Trust() {
  return (
    <section className="trust" id="trust">
      <div className="wrap" style={{ textAlign: "center" }}>
        <Reveal><div className="overline">Every sign, built to the same standard</div></Reveal>
        <div className="trust-row">
          {TRUST.map((t, i) => (
            <Reveal key={t} delay={i * 70}><span>{t}</span></Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Tiers() {
  return (
    <section className="sect" id="pricing">
      <div className="wrap">
        <SectionHead
          overline="Pricing"
          title="Three ways in."
          lede="Every sign is made to order. Pick a lane — the design call locks the final quote."
        />
        <div className="tiers">
          {TIERS.map((t, i) => (
            <Reveal key={t.name} delay={i * 70}>
              <div className={`card ${t.featured ? "feat" : ""}`}>
                {t.tag && <span className="tag">{t.tag}</span>}
                <div className="tier-name">{t.name}</div>
                <div className="price"><small>{t.from} </small>{t.price}</div>
                <p>{t.blurb}</p>
                <ul className="feats">
                  {t.feats.map((f) => (
                    <li key={f}><Check size={14} />{f}</li>
                  ))}
                </ul>
                <button
                  className={`btn ${t.featured ? "btn-neon" : "btn-ghost"}`}
                  style={{ justifyContent: "center" }}
                  onClick={() => go("book")}
                >
                  Start with {t.name}
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Gallery() {
  return (
    <section className="sect" id="work" style={{ paddingTop: 0 }}>
      <div className="wrap">
        <hr className="rule" style={{ marginBottom: 120 }} />
        <SectionHead
          overline="Recent builds"
          title="The work, after dark."
          lede="Real signs from the Peenya workshop, shot in the spaces they now light. Photos drop in as each build ships."
        />
        <div className="masonry">
          {GALLERY.map((g, i) => (
            <Reveal key={i} delay={(i % 3) * 70}>
              <div className="tile" style={g.src ? undefined : { minHeight: g.h }}>
                {g.src ? (
                  <>
                    <img src={g.src} alt={g.caption || "Neon Forge sign"} loading="lazy" />
                    {(g.caption || g.dim) && (
                      <div className="cap"><b>{g.caption}</b><span>{g.dim}</span></div>
                    )}
                  </>
                ) : (
                  <div className="ph">
                    <ImageIcon size={22} />
                    <span>Photo coming soon</span>
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Process() {
  return (
    <section className="sect" id="process" style={{ paddingTop: 0 }}>
      <div className="wrap">
        <hr className="rule" style={{ marginBottom: 120 }} />
        <SectionHead
          overline="How it works"
          title="Design. Forge. Deliver."
          lede="One conversation to kick off. We handle everything between the idea and the glow."
        />
        <div className="steps">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 70}>
              <div className="step">
                <div className="step-n">{s.n}</div>
                <div className="step-ic"><s.Icon size={20} /></div>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Configurator() {
  const [text, setText] = useState("open late");
  const [color, setColor] = useState(COLORS[0]);
  const [size, setSize] = useState(SIZES[1]);

  const clean = text.trim() || "your words";
  const est = size.base + Math.max(0, clean.length - 8) * 900;

  return (
    <section className="sect" id="design" style={{ paddingTop: 0 }}>
      <div className="wrap">
        <hr className="rule" style={{ marginBottom: 120 }} />
        <SectionHead
          overline="Configurator"
          title="See it glowing first."
          lede="Type it, tint it, size it. The real build starts with a proper mockup on the design call."
        />
        <div className="config">
          <div className="panel">
            <div className="field">
              <label className="label" htmlFor="nf-text">Your words</label>
              <input
                id="nf-text"
                className="tinput"
                value={text}
                maxLength={20}
                onChange={(e) => setText(e.target.value)}
                placeholder="open late"
              />
            </div>
            <div className="field">
              <span className="label">Tube colour</span>
              <div className="swatches">
                {COLORS.map((c) => (
                  <button
                    key={c.name}
                    className={`sw ${color.name === c.name ? "on" : ""}`}
                    style={{ background: c.chip, "--sg": c.g }}
                    onClick={() => setColor(c)}
                    aria-label={c.name}
                    title={c.name}
                  />
                ))}
              </div>
            </div>
            <div className="field" style={{ marginBottom: 8 }}>
              <span className="label">Size</span>
              <div className="seg">
                {SIZES.map((s) => (
                  <button
                    key={s.id}
                    className={size.id === s.id ? "on" : ""}
                    onClick={() => setSize(s)}
                  >
                    {s.label}<small>{s.sub}</small>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="preview">
            <div className="brick" aria-hidden="true" />
            <div
              className="tube tube-live"
              style={{
                fontSize: size.fs,
                color: color.core,
                "--g": color.g,
                wordBreak: "break-word",
                maxWidth: "100%",
              }}
            >
              {clean}
            </div>
            <div className="est">
              <span>Estimate · final quote on call</span>
              <b>{size.id === "L" ? `${inr(est)}+` : inr(est)}</b>
            </div>
          </div>
        </div>
        <Reveal delay={120}>
          <div style={{ marginTop: 36 }}>
            <button className="btn btn-neon" onClick={() => go("book")}>
              Take this to a design call <ArrowRight size={16} />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Why() {
  return (
    <section className="sect" style={{ paddingTop: 0 }}>
      <div className="wrap">
        <hr className="rule" style={{ marginBottom: 120 }} />
        <SectionHead
          overline="Why Neon Forge"
          title="Made properly, not fast and cheap."
          lede="No customer quotes yet — we're new. So here's exactly what you get instead."
        />
        <div className="quotes">
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={i * 70}>
              <div className="quote">
                <div className="why-ic"><w.Icon size={20} /></div>
                <h3 className="why-h">{w.title}</h3>
                <p>{w.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Booking() {
  return (
    <section className="sect" id="book" style={{ paddingTop: 0 }}>
      <div className="wrap">
        <hr className="rule" style={{ marginBottom: 120 }} />
        <div className="book">
          <div>
            <SectionHead
              overline="Book"
              title="Let's build yours."
              lede="Fifteen minutes is all it takes to scope a sign. Here's the whole runway:"
            />
            <ul className="funnel" style={{ marginTop: 0 }}>
              <li>
                <span className="fn">01</span>
                <div>
                  <h4>Book a 15-minute call</h4>
                  <p>Pick a slot below. Tell us about the space, the wall, the vibe.</p>
                </div>
              </li>
              <li>
                <span className="fn">02</span>
                <div>
                  <h4>Share the brief</h4>
                  <p>A short form for fonts, colours and reference shots — takes three minutes.</p>
                </div>
              </li>
              <li>
                <span className="fn">03</span>
                <div>
                  <h4>Approve &amp; forge</h4>
                  <p>Sign off the mockup, pay 50% to start the build. Balance on delivery.</p>
                </div>
              </li>
            </ul>
          </div>

          {/* ── Swap this panel for the live Calendly inline embed ──
               <InlineWidget url="https://calendly.com/YOUR-HANDLE/design-call" />
               Typeform + Razorpay links fire from the email sequence after booking. */}
          <div className="cal-box">
            <Calendar size={30} style={{ color: "rgba(255,45,149,.8)" }} />
            <div style={{ fontFamily: "'Unbounded',sans-serif", fontWeight: 600, fontSize: 16, color: "#fff" }}>
              Live scheduling embeds here
            </div>
            <p style={{ color: "var(--muted)", fontSize: 14, maxWidth: "26rem" }}>
              This panel becomes the Calendly inline widget — call slots, timezone-aware, synced to the workshop calendar.
            </p>
            <span className="mono">calendly.com/neonforge/design-call</span>
            <a className="btn btn-neon" href="https://calendly.com/" target="_blank" rel="noreferrer">
              Open booking link <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="wrap">
        <div className="f-grid">
          <div className="f-col">
            <div className="logo" style={{ marginBottom: 18 }}><i /> NEON FORGE</div>
            <p style={{ color: "var(--muted)", fontSize: 14, maxWidth: "24rem" }}>
              Handcrafted LED neon from Peenya, Bengaluru. Designed to be
              photographed, built to run for years.
            </p>
          </div>
          <div className="f-col">
            <h5>Workshop</h5>
            <ul>
              <li><MapPin size={15} /> Peenya Industrial Area, Bengaluru</li>
              <li><Mail size={15} /> hello@neonforge.in</li>
            </ul>
          </div>
          <div className="f-col">
            <h5>Follow the glow</h5>
            <ul>
              <li><a href="https://instagram.com" target="_blank" rel="noreferrer">Instagram</a></li>
              <li><a href="https://wa.me/" target="_blank" rel="noreferrer">WhatsApp</a></li>
            </ul>
          </div>
        </div>
        <div className="f-base">
          <span>© 2026 Neon Forge · Made in Bengaluru</span>
          <span>Ships pan-India · Installs in Bengaluru</span>
        </div>
      </div>
    </footer>
  );
}

/* ── app ──────────────────────────────────────────────────────────────── */

export default function NeonForge() {
  const cursorRef = useRef(null);

  /* Cursor glow trail — desktop only. Production equivalent: GSAP quickTo */
  useEffect(() => {
    const fine = window.matchMedia("(pointer: fine)").matches;
    if (!fine || prefersReduced()) return;
    const el = cursorRef.current;
    if (!el) return;
    let tx = window.innerWidth / 2, ty = window.innerHeight / 3;
    let x = tx, y = ty, raf, shown = false;
    const move = (e) => {
      tx = e.clientX; ty = e.clientY;
      if (!shown) { el.style.opacity = "1"; shown = true; }
    };
    const tick = () => {
      x += (tx - x) * 0.12;
      y += (ty - y) * 0.12;
      el.style.transform = `translate(${x - 260}px, ${y - 260}px)`;
      raf = requestAnimationFrame(tick);
    };
    window.addEventListener("pointermove", move, { passive: true });
    tick();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", move);
    };
  }, []);

  return (
    <div className="nf">
      <style>{CSS}</style>
      <div className="pools" aria-hidden="true" />
      <div className="grain" aria-hidden="true" />
      <div ref={cursorRef} className="cursor-glow" aria-hidden="true" />

      <Nav />
      <main>
        <Hero />
        <Trust />
        <Tiers />
        <Gallery />
        <Process />
        <Configurator />
        <Why />
        <Booking />
      </main>
      <Footer />
    </div>
  );
}